import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { FormSelectField, lt } from '@mews/framework';
import { Amount, WithAccountId } from '@mews-commander/types';
import { OrderItem, Reservation } from '@mews-commander/data-access';
import { ConvertedCurrencyValue, LocalizedDateFromUtc } from '@mews-commander/ui';
import { UnicodeEntity } from '@mews/enums';
import { DropdownItem, ListDivider, ListHeadline, Typography } from '@optimus-web/core';
import { sumAllAmounts } from '@mews-commander/utils';
import {
    CommonProcessPaymentFormModel,
    ProcessPaymentFormField,
} from '../forms/ProcessPayment/ProcessPaymentFormSchema';
import { useGetOrderItemsSuspense, useGetPaymentPlansForReservationSuspense } from '../tables/PaymentPlans/hooks';
import { useGetAllReservationsSuspense } from '../ReservationAsyncSelect/queries';
import { useDefaultFormContext } from '../forms/ProcessPayment/useDefaultFormValues';
import { getAccountableOrderItemByReservation } from '../tables/PaymentPlans/utils';
import { getDefaultPaymentPlanId } from './utils';

const PaymentPlanReservationSelectItemSecondary = ({
    orderItems,
    startUtc,
    endUtc,
}: { orderItems: OrderItem[] } & Reservation) => {
    const { defaultCurrencyCode } = useCurrentEnterprise();

    return (
        <Typography textStyle="bodySmallStrong" variant="tertiary" as="div">
            <LocalizedDateFromUtc utcString={startUtc} />
            {UnicodeEntity.NoBreakSpace}
            {UnicodeEntity.Dash}
            {UnicodeEntity.NoBreakSpace}
            <LocalizedDateFromUtc utcString={endUtc} />
            {UnicodeEntity.NoBreakSpace}
            {UnicodeEntity.Pipe}
            {UnicodeEntity.NoBreakSpace}
            <ConvertedCurrencyValue
                amount={sumAllAmounts(
                    orderItems.map(({ totalAmount }) => totalAmount) as Amount[],
                    defaultCurrencyCode
                )}
            />
        </Typography>
    );
};

export const LinkPaymentPlanToPaymentSelect = ({ accountId }: WithAccountId) => {
    const {
        defaultFormValues: { paymentPlanId: defaultFormPaymentPlanId },
        readOnlyFormFields: { isPaymentPlanIdReadOnly },
        setPaymentPlanId,
    } = useDefaultFormContext();

    const { setValue, watch } = useFormContext<CommonProcessPaymentFormModel>();
    const serviceOrderId = watch(ProcessPaymentFormField.ServiceOrderId);

    const reservations = useGetAllReservationsSuspense({ accountId });
    const selectedReservation = reservations.data?.reservations?.find(({ id }) => id === serviceOrderId);
    const paymentPlansForSelectedReservation = useGetPaymentPlansForReservationSuspense({ accountId, serviceOrderId });
    const orderItems = useGetOrderItemsSuspense({ accountId });

    const defaultPaymentPlanId = getDefaultPaymentPlanId(paymentPlansForSelectedReservation, defaultFormPaymentPlanId);

    useEffect(() => {
        setValue(ProcessPaymentFormField.PaymentPlanId, defaultPaymentPlanId);
    }, [defaultPaymentPlanId, serviceOrderId, setValue]);

    if (!selectedReservation || paymentPlansForSelectedReservation.length === 0) {
        return null;
    }

    return (
        <FormSelectField
            name={ProcessPaymentFormField.PaymentPlanId}
            label={lt('ProcessPaymentLinkPaymentToPaymentPlanSelectLabel')}
            defaultValue={defaultPaymentPlanId}
            readOnly={isPaymentPlanIdReadOnly}
            onChange={(_, id) => setPaymentPlanId(id as string)}
        >
            <DropdownItem
                key="empty-value"
                // @ts-expect-error it's better to explicitly set empty value as `null` to distinguish between `null` as this list item and `undefined` as no list item selected
                value={null}
                label={lt('ProcessPaymentLinkPaymentToPaymentPlanSelectEmptyItemPrimary')}
            />
            <ListDivider />
            <ListHeadline>{lt('ProcessPaymentLinkPaymentToPaymentPlanSelectHeadline')}</ListHeadline>

            {paymentPlansForSelectedReservation.map(({ id, name }) => (
                <DropdownItem
                    label={name}
                    description={
                        <PaymentPlanReservationSelectItemSecondary
                            orderItems={getAccountableOrderItemByReservation(orderItems, selectedReservation)}
                            {...selectedReservation}
                        />
                    }
                    value={id}
                    key={id}
                />
            ))}
        </FormSelectField>
    );
};
